import Sketch from "@arcgis/core/widgets/Sketch";
import Zoom from "@arcgis/core/widgets/Zoom";
import React, { MutableRefObject } from "react";
import { SketchTool } from "../../hooks/editor/useSketchEvents";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDrawPolygon,
  faMinus,
  faMousePointer,
  faPlus,
  faRedo,
  faTrashAlt,
  faUndo,
} from "@fortawesome/free-solid-svg-icons";
import { faCircle, faSquare } from "@fortawesome/free-regular-svg-icons";
import { Popup } from "semantic-ui-react";
import classNames from "classnames";
import { faPen } from "@fortawesome/pro-solid-svg-icons";

interface ToolbarProps {
  sketchRef: MutableRefObject<Sketch | null>;
  zoomRef: MutableRefObject<Zoom | null>;
  handleToolSelect: (tool: SketchTool) => void;
  selectedTool: SketchTool;
  handleUndo: () => void;
  handleRedo: () => void;
  canUndo: boolean;
  canRedo: boolean;
}
export function MapEditorToolbar({
  sketchRef,
  zoomRef,
  handleToolSelect,
  selectedTool,
  handleUndo,
  handleRedo,
  canUndo,
  canRedo,
}: ToolbarProps) {
  const toolbarToolBase =
    "tw-px-2 tw-py-2 tw-bg-white tw-border-none tw-shadow-md tw-flex tw-items-center tw-justify-center";
  const toolbarToolClickable =
    "tw-px-2 tw-py-2 hover:tw-text-blue active:tw-bg-blue active:tw-text-white tw-transition-colors tw-flex tw-items-center tw-justify-center";
  const toolbarToolSelectable = (tool: SketchTool) =>
    classNames({
      [toolbarToolBase]: selectedTool !== tool,
      [toolbarToolClickable]: selectedTool !== tool,
      "tw-px-2 tw-py-2 tw-border-none tw-shadow-md tw-bg-blue tw-text-white tw-flex tw-items-center tw-justify-center":
        selectedTool === tool,
    });
  return (
    <div className='tw-absolute tw-left-2 tw-top-2 tw-z-10 '>
      <div className='tw-flex tw-overflow-hidden tw-bg-transparent tw-shadow-md'>
        {/* Undo and Redo */}
        <Popup
          content='Undo'
          trigger={
            <button
              className={`${toolbarToolBase} ${canUndo ? toolbarToolClickable : ""} tw-rounded-l-md `}
              onClick={handleUndo}
              disabled={!canUndo}
            >
              <FontAwesomeIcon icon={faUndo} />
            </button>
          }
          position='bottom left'
        />
        <Popup
          content='Redo'
          trigger={
            <button
              className={`${toolbarToolBase} ${canRedo ? toolbarToolClickable : ""} tw-rounded-r-md `}
              onClick={handleRedo}
              disabled={!canRedo}
            >
              <FontAwesomeIcon icon={faRedo} />
            </button>
          }
          position='bottom center'
        />

        {/* Delete */}
        <Popup
          content='Delete'
          trigger={
            <button
              className={`${toolbarToolBase} ${toolbarToolClickable} tw-ml-2 tw-rounded-md `}
              onClick={() => sketchRef.current?.delete()}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
            </button>
          }
          position='bottom center'
        />

        {/* Selection tools and shapes */}
        <Popup
          content='Select'
          trigger={
            <button
              className={`${toolbarToolSelectable(SketchTool.Select)} tw-ml-2 tw-rounded-l-md`}
              onClick={() => handleToolSelect(SketchTool.Select)}
            >
              <FontAwesomeIcon icon={faMousePointer} />
            </button>
          }
          position='bottom center'
        />
        <Popup
          content='Edit'
          trigger={
            <button
              className={`${toolbarToolSelectable(SketchTool.Edit)} tw-mr-2 tw-rounded-r-md`}
              onClick={() => handleToolSelect(SketchTool.Edit)}
            >
              <FontAwesomeIcon icon={faPen} />
            </button>
          }
          position='bottom center'
        />
        <Popup
          content='Draw Polygon'
          trigger={
            <button
              className={`${toolbarToolSelectable(SketchTool.Polygon)} tw-rounded-l-md`}
              onClick={() => handleToolSelect(SketchTool.Polygon)}
            >
              <FontAwesomeIcon icon={faDrawPolygon} />
            </button>
          }
          position='bottom center'
        />
        <Popup
          content='Draw Rectangle'
          trigger={
            <button
              className={`${toolbarToolSelectable(SketchTool.Rectangle)} `}
              onClick={() => handleToolSelect(SketchTool.Rectangle)}
            >
              <FontAwesomeIcon icon={faSquare} />
            </button>
          }
          position='bottom center'
        />
        <Popup
          content='Draw Circle'
          trigger={
            <button
              className={`${toolbarToolSelectable(SketchTool.Circle)} tw-rounded-r-md `}
              onClick={() => handleToolSelect(SketchTool.Circle)}
            >
              <FontAwesomeIcon icon={faCircle} />
            </button>
          }
          position='bottom center'
        />

        {/* Zoom controls */}
        <Popup
          content='Zoom Out'
          trigger={
            <button
              className={`${toolbarToolBase} ${toolbarToolClickable} tw-ml-2 tw-rounded-md tw-px-2 tw-py-2 `}
              onClick={() => zoomRef.current?.zoomOut()}
            >
              <FontAwesomeIcon icon={faMinus} />
            </button>
          }
          position='bottom center'
        />
        <Popup
          content='Zoom In'
          trigger={
            <button
              className={`${toolbarToolBase} ${toolbarToolClickable} tw-ml-2 tw-rounded-md tw-px-2 tw-py-2 `}
              onClick={() => zoomRef.current?.zoomIn()}
            >
              <FontAwesomeIcon icon={faPlus} />
            </button>
          }
          position='bottom center'
        />
      </div>
    </div>
  );
}
